import { usePathname, useRouter, useSegments } from "expo-router"
import React from "react"
import { Pressable, StyleSheet, View } from "react-native"

import { getDeviceProfileBasePages } from "@/components/device-profile/device-profile-pages"
import { DeviceCycler } from "@/components/device-profile/DeviceCycler"
import { useDeviceProfileContext } from "@/components/DeviceProfileContext"
import { InstallationTypeIndicator } from "@/components/InstallationTypeIndicator"
import { Tab } from "@/components/Tab"
import {
  AppIcons,
  AppText,
  Box,
  COLORS,
  Container,
  Heading,
  LinkText,
  RADII,
  Row,
  testIds,
  useBreakpointValue,
  useIsDarkMode,
} from "@/theme"
import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"
import { Theme } from "@/theme/theme"

import type { BoxProps } from "@/theme"

const styles = StyleSheet.create({
  dropdownButton: {
    alignItems: "center",
    borderColor: COLORS.$divider.light,
    borderRadius: RADII.$default,
    borderWidth: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: Theme.SPACING.$3,
    paddingVertical: Theme.SPACING.$2,
  },
  dropdownButtonDark: {
    backgroundColor: COLORS.$paper.dark,
    borderColor: COLORS.$divider.dark,
  },
  dropdownButtonLight: {
    backgroundColor: COLORS.$paper.light,
  },
  dropdownItem: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    paddingHorizontal: Theme.SPACING.$3,
    paddingVertical: Theme.SPACING.$2,
  },
  dropdownItemHovered: {
    backgroundColor: Theme.COLORS.$blue[200],
  },
  dropdownItemHoveredDark: {
    backgroundColor: Theme.COLORS.$gray[700],
  },
  dropdownItemSelected: {
    backgroundColor: COLORS.$yellow[400],
  },
  dropdownItemSelectedDark: {
    backgroundColor: Theme.COLORS.$green[800],
  },
  dropdownMenu: {
    borderColor: COLORS.$divider.light,
    borderRadius: RADII.$default,
    borderWidth: 1,
    left: 0,
    marginTop: 4,
    position: "absolute",
    right: 0,
    top: "100%",
    zIndex: 100,
  },
  dropdownMenuDark: {
    backgroundColor: COLORS.$paper.dark,
    borderColor: COLORS.$divider.dark,
  },
  dropdownMenuLight: {
    backgroundColor: COLORS.$paper.light,
  },
  dropdownOverlay: {
    bottom: 0,
    left: 0,
    position: "fixed" as unknown as undefined,
    right: 0,
    top: 0,
    zIndex: 99,
  },
  dropdownWrapper: {
    position: "relative",
    zIndex: 10,
  },
  iconText: {
    marginLeft: Theme.SPACING.$1,
  },
  selectedLabel: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    flexDirection: "row",
  },
  tabContainerDark: {
    backgroundColor: COLORS.$paper.dark,
  },
  tabLinksContainer: {
    position: "relative",
    zIndex: 10,
  },
  tabsContainer: {
    backgroundColor: COLORS.$paper.light,
    borderRadius: RADII.$default,
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
})

function useTabNavigation() {
  const device = useDeviceProfileContext()
  const { codaDeviceAlias } = device
  const basePath = `/devices/${device.codaDeviceAlias}/`
  const currentPath = usePathname()
  const chunks: string[] = useSegments()
  const currentEndSegment = chunks[4]
  const pages = getDeviceProfileBasePages({ codaDeviceAlias })

  return pages.map((page, index) => {
    let isSelected = false
    let targetPath: string

    if (typeof page.href === "string") {
      targetPath = page.href
    } else {
      targetPath = page.href.pathname
    }
    const linkEndSegment = targetPath.split("/").pop()

    if (index === 0) {
      isSelected =
        currentPath === basePath || `${currentPath}/` === basePath
    } else if (linkEndSegment === currentEndSegment) {
      isSelected = true
    }

    return { ...page, isSelected }
  })
}

function TabDropdown() {
  const isDark = useIsDarkMode()
  const navigate = useRouter()
  const [isOpen, setIsOpen] = React.useState(false)
  const pages = useTabNavigation()
  const selectedPage = pages.find((p) => p.isSelected) ?? pages[0]
  if (selectedPage === undefined) {
    return null
  }

  return (
    <View {...testIds("navMenu")} style={styles.dropdownWrapper}>
      <Pressable
        {...testIds("navMenuToggleBtn")}
        style={[
          styles.dropdownButton,
          isDark ? styles.dropdownButtonDark : styles.dropdownButtonLight,
        ]}
        onPress={() => setIsOpen((prev) => !prev)}
      >
        <View style={styles.selectedLabel}>
          {renderIconFromIconProp(selectedPage.IconComponent, {
            color: isDark ? COLORS.$link.dark : COLORS.$link.light,
            size: Theme.SIZES.$3,
          })}
          <LinkText style={styles.iconText}>{selectedPage.name}</LinkText>
        </View>
        <AppIcons.ChevronDown />
      </Pressable>
      {isOpen ? (
        <React.Fragment>
          <Pressable
            style={styles.dropdownOverlay}
            onPress={() => setIsOpen(false)}
          />
          <View
            style={[
              styles.dropdownMenu,
              isDark ? styles.dropdownMenuDark : styles.dropdownMenuLight,
            ]}
          >
            {pages.map((page) => (
              <Pressable
                key={page.id}
                {...testIds(`${page.id}Link`)}
                style={(state) => {
                  const { hovered = false } =
                    state as unknown as { hovered?: boolean }
                  if (isDark) {
                    return [
                      styles.dropdownItem,
                      hovered && styles.dropdownItemHoveredDark,
                      page.isSelected && styles.dropdownItemSelectedDark,
                    ]
                  }
                  return [
                    styles.dropdownItem,
                    hovered && styles.dropdownItemHovered,
                    page.isSelected && styles.dropdownItemSelected,
                  ]
                }}
                onPress={() => {
                  setIsOpen(false)
                  navigate.navigate(page.href)
                }}
              >
                {renderIconFromIconProp(page.IconComponent, {
                  color: isDark ? COLORS.$link.dark : COLORS.$link.light,
                  size: Theme.SIZES.$3,
                })}
                <LinkText style={styles.iconText}>{page.name}</LinkText>
              </Pressable>
            ))}
          </View>
        </React.Fragment>
      ) : null}
    </View>
  )
}

function TabRow() {
  const isDark = useIsDarkMode()
  const navigate = useRouter()
  const pages = useTabNavigation()

  return (
    <View
      {...testIds("navMenu")}
      style={[styles.tabsContainer, isDark && styles.tabContainerDark]}
    >
      {pages.map((page) => (
        <Tab
          key={page.id}
          IconComponent={page.IconComponent}
          id={`${page.id}Link`}
          isSelected={page.isSelected}
          text={page.name}
          onPress={() => navigate.navigate(page.href)}
        />
      ))}
    </View>
  )
}

function TabLinks() {
  const getValue = useBreakpointValue()
  const useDropdown = getValue({ base: true, md: false }) === true

  if (useDropdown) {
    return <TabDropdown />
  }
  return <TabRow />
}

export interface DeviceProfilePageProps {
  showCycler?: boolean
}

export default function DeviceProfilePage({
  children,
  showCycler = false,
  ...rest
}: BoxProps & DeviceProfilePageProps) {
  const getValue = useBreakpointValue()
  const { codaDeviceAlias, deviceInstallationType, deviceName } =
    useDeviceProfileContext()
  return (
    <Box flex={1} maxW={"$5xl"} mx={"auto"} w={"$full"} {...rest}>
      <Container>
        <Row
          alignItems={"flex-start"}
          flexWrap={"nowrap"}
          justifyContent={"space-between"}
          pb={"$2"}
          pt={"$2"}
          space={"$4"}
        >
          <Row alignItems={"flex-start"} flex={1}>
            <Box flex={1} mr={"$2"}>
              <Heading
                isTruncated
                variant={getValue({ base: "h5", md: "h4" })}
              >
                {deviceName}
              </Heading>
              {codaDeviceAlias === deviceName ? null : (
                <AppText
                  isTruncated
                  colorScheme={"secondary"}
                  fontSize={"$sm"}
                >
                  {codaDeviceAlias}
                </AppText>
              )}
            </Box>
            <InstallationTypeIndicator value={deviceInstallationType} />
          </Row>
          {showCycler ? (
            <DeviceCycler codaDeviceAlias={codaDeviceAlias} />
          ) : null}
        </Row>
      </Container>
      <Container pb={"$2"} style={styles.tabLinksContainer}>
        <TabLinks />
      </Container>
      {children}
    </Box>
  )
}
